<template>
  <div v-loading="loading" class="additional-info-tab">
    <div class="row mb-2" v-if="validationErrors">
      <div class="col">
        <el-alert type="error">
          {{ validationErrors }}
        </el-alert>
      </div>
    </div>

        <!-- Budget Breakdown Section -->
    <div class="row">
      <div class="col-12">
        <base-input 
          label="What is the client's budget breakdown? (optional)"
        >
          
          <div class="row">
            <div class="col-md-6">
              <base-input 
                label="F&B Budget"
                type="number"
                v-model="form.f_and_b_budget"
                placeholder="$0"
              />
            </div>
            <div class="col-md-6">
              <base-input 
                label="Accommodation Budget"
                type="number"
                v-model="form.accommodation_budget"
                placeholder="$0"
              />
            </div>
          </div>
          
          <div class="row mt-3">
            <div class="col-md-6">
              <base-input 
                label="DDP Budget (per head)"
                type="number"
                v-model="form.ddp_budget"
                placeholder="$0"
              />
            </div>
            <div class="col-md-6">
              <base-input 
                label="AV Budget"
                type="number"
                v-model="form.av_budget"
                placeholder="$0"
              />
            </div>
          </div>
        </base-input>
      </div>
    </div>

    <!-- Previous Event Experience Section -->
    <div class="row mt-4">
      <div class="col-12">
        <base-input 
          label="Has the client hosted this event previously?"
        >
          <el-radio-group v-model="form.previous_occurrence">
            <el-radio :label="false">No</el-radio>
            <el-radio :label="true">Yes</el-radio>
          </el-radio-group>
        </base-input>

        <!-- Show venue input if Yes is selected -->
        <div v-if="form.previous_occurrence" class="mt-3">
          <base-input 
            label="Where did the client host their last event?"
            v-model="form.previous_venue"
            placeholder="Enter venue name"
          />
        </div>
      </div>
    </div>

    <!-- Venue Lock-in Timeline Section -->
    <div class="row mt-4">
      <div class="col-12">
        <base-input 
          label="When is the client wanting to lock in their venue?"
        >
          <el-select 
            v-model="form.booking_urgency" 
            placeholder="Immediately"
            :clearable="true"
          >
            <el-option
              v-for="option in venueLockTimelineOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </base-input>
      </div>
    </div>

    <!-- Save Button -->
    <div class="row mt-4">
      <div class="col-12 text-right">
        <base-button 
          type="primary" 
          @click="saveAdditionalInfo"
          :loading="loading"
        >
          Save & Finish
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Notification,
  Radio,
  RadioGroup
} from "element-ui";
import { mapState } from "vuex";
import LeadEventService from '@/services/lead-event.service';
import leadEventFormMixin from '@/helpers/leadEventFormMixin';

export default {
  name: 'AdditionalInfo',
  mixins: [leadEventFormMixin],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Notification.name]: Notification,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      loading: false,
      validationErrors: '',
      fieldErrors: {},
      form: {
        step: 4,
        direct_update: true,
        f_and_b_budget: null,
        accommodation_budget: null,
        ddp_budget: null,
        av_budget: null,
        previous_occurrence: null,
        previous_venue: null,
        booking_urgency: null,
      },
      venueLockTimelineOptions: [],
    };
  },
  computed: {
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
  },
  methods: {
    async saveAdditionalInfo() {
      this.loading = true;
      try {
        // Send form data directly to backend
        await LeadEventService.updateCorporateLeadEvent(this.leadEvent.id, this.form);

        // Emit success event to parent
        this.$emit('onSave', {
          message: 'Additional info saved successfully',
        });
        
        // Show success message
        Notification.success({
          title: 'Success',
          message: 'Additional info saved successfully!',
          type: 'success',
        });
        
      } catch (error) {
        console.error('Error saving additional info:', error);
        // Show error message
        Notification.error({
          title: 'Error',
          message: 'Failed to save additional info. Please check your inputs and try again.',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    
    updateFormFromLeadEvent(leadEvent) {
      // Setting default options for venue lock timeline.
      this.venueLockTimelineOptions = leadEvent?.options?.booking_urgency || [];

      // Update form fields from leadEvent data, preserving existing values when new data is missing
      const safeUpdate = (currentValue, newValue) => {
        return (newValue !== null && newValue !== undefined && newValue !== '') ? newValue : currentValue;
      };

      // Handle has happened before boolean conversion
      let hasHappenedBefore = this.safeBooleanUpdate(leadEvent.has_happened_before) || false;

      this.form.f_and_b_budget = safeUpdate(this.form.f_and_b_budget, leadEvent.f_and_b_budget);
      this.form.accommodation_budget = safeUpdate(this.form.accommodation_budget, leadEvent.accommodation_budget);
      this.form.ddp_budget = safeUpdate(this.form.ddp_budget, leadEvent.per_head_ddp_budget);
      this.form.av_budget = safeUpdate(this.form.av_budget, leadEvent.av_budget);
      this.form.previous_occurrence = safeUpdate(this.form.previous_occurrence, hasHappenedBefore);
      this.form.previous_venue = safeUpdate(this.form.previous_venue, leadEvent.last_hosting_venue);
      this.form.booking_urgency = safeUpdate(this.form.booking_urgency, leadEvent.venue_booking_urgency);

      console.log('Additional info form updated with leadEvent data:', this.form);
    }
  },
  mounted() {
    // Initialize form with leadEvent data when component is mounted
    if (this.leadEvent) {
      this.updateFormFromLeadEvent(this.leadEvent);
    }
  },
  watch: {
    leadEvent: {
      handler(newLeadEvent) {
        if (newLeadEvent) {
          this.updateFormFromLeadEvent(newLeadEvent);
        }
      },
      deep: true,
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.additional-info-tab {
  .el-radio {
    margin-right: 20px;
  }
}
</style>
